import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  SUCCESSOR_ENTITY_TYPES,
  SUCCESSOR_STATUS_TYPES,
  filterSuccessorTypes,
  type SuccessorChooserKey,
  type SuccessorTypeOption
} from '@/lib/successorEntityMapping';
import { TypePickerNav, type TypePickerNavSectionInput } from '../type-picker/TypePickerNav';

interface SuccessorTypeSidebarProps {
  currentType: SuccessorChooserKey;
  onSelect: (type: SuccessorChooserKey) => void;
  hideExternalContact?: boolean;
  hideNone?: boolean;
  hideUnclear?: boolean;
}

export function SuccessorTypeSidebar({
  currentType,
  onSelect,
  hideExternalContact,
  hideNone,
  hideUnclear
}: SuccessorTypeSidebarProps) {
  const { t } = useTranslation();

  const sections = useMemo<TypePickerNavSectionInput<SuccessorChooserKey>[]>(() => {
    const toOption = ({ value, icon, labelKey }: SuccessorTypeOption) => ({
      value,
      icon,
      label: t(labelKey)
    });

    const entityOptions = filterSuccessorTypes(SUCCESSOR_ENTITY_TYPES, {
      hideExternalContact
    }).map(toOption);
    const statusOptions = filterSuccessorTypes(SUCCESSOR_STATUS_TYPES, {
      hideNone,
      hideUnclear
    }).map(toOption);

    const result: TypePickerNavSectionInput<SuccessorChooserKey>[] = [
      {
        id: 'entity',
        heading: t('entityPicker.successor.sidebarTypeHeading'),
        options: entityOptions
      }
    ];
    if (statusOptions.length > 0) {
      result.push({ id: 'status', options: statusOptions });
    }
    return result;
  }, [hideExternalContact, hideNone, hideUnclear, t]);

  return <TypePickerNav sections={sections} currentType={currentType} onSelect={onSelect} />;
}
